import { createElement, Fragment, type ReactNode } from 'react';
import { Link } from 'react-router';

export interface LegalSection {
  id: string;
  title: string;
  body: ReactNode;
}

export interface ParsedLegalDoc {
  /** Text before the first heading, shown as the subtitle under the document title. */
  intro: ReactNode | null;
  sections: LegalSection[];
}

/**
 * Turns an operator-written legal document into what the legal pages render. The format is plain text with a
 * little structure: text before the first heading is the subtitle; a line starting with `## ` begins a section;
 * blank lines separate paragraphs; lines starting with `- ` form a bulleted list; `**bold**` and `[label](url)`
 * work inline (a URL starting with `/` stays inside the app). Placeholders such as {tenant} are filled in by the API.
 */
export function parseLegalText(text: string): ParsedLegalDoc {
  const intro: string[] = [];
  const sections: Array<{ title: string; lines: string[] }> = [];
  for (const raw of text.replace(/\r\n/g, '\n').split('\n')) {
    const heading = /^##\s+(.+?)\s*$/.exec(raw);
    if (heading) {
      sections.push({ title: heading[1] ?? 'Section', lines: [] });
      continue;
    }
    if (sections.length === 0) intro.push(raw);
    else sections[sections.length - 1]!.lines.push(raw);
  }
  const used = new Set<string>();
  return {
    intro: intro.some((l) => l.trim()) ? renderInline(intro.map((l) => l.trim()).filter(Boolean).join(' ')) : null,
    sections: sections.map((s, i) => {
      let id = s.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || `section-${i + 1}`;
      while (used.has(id)) id = `${id}-${i + 1}`;
      used.add(id);
      return { id, title: s.title, body: renderBlocks(s.lines) };
    }),
  };
}

function renderBlocks(lines: string[]): ReactNode {
  const blocks: ReactNode[] = [];
  let paragraph: string[] = [];
  let bullets: string[] = [];
  const flush = () => {
    if (paragraph.length) {
      blocks.push(createElement('p', { key: blocks.length }, renderInline(paragraph.join(' '))));
      paragraph = [];
    }
    if (bullets.length) {
      blocks.push(createElement('ul', { key: blocks.length }, bullets.map((b, i) => createElement('li', { key: i }, renderInline(b)))));
      bullets = [];
    }
  };
  for (const line of lines) {
    const t = line.trim();
    if (!t) {
      flush();
      continue;
    }
    const bullet = /^[-*•]\s+(.+)$/.exec(t);
    if (bullet) {
      if (paragraph.length) flush();
      bullets.push(bullet[1] ?? '');
      continue;
    }
    if (bullets.length) flush();
    paragraph.push(t);
  }
  flush();
  return createElement(Fragment, null, ...blocks);
}

const INLINE = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)\s]+\))/g;

/** Bold and links inside a paragraph or bullet. */
function renderInline(text: string): ReactNode {
  const parts = text.split(INLINE).filter((p) => p !== '');
  if (parts.length === 1 && !INLINE.test(text)) return text;
  return createElement(
    Fragment,
    null,
    ...parts.map((part, i) => {
      const bold = /^\*\*([^*]+)\*\*$/.exec(part);
      if (bold) return createElement('span', { key: i, className: 'font-medium' }, bold[1]);
      const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part);
      if (link) {
        const [, label = '', href = ''] = link;
        const cls = 'underline underline-offset-4';
        return href.startsWith('/') ? createElement(Link, { key: i, to: href, className: cls }, label) : createElement('a', { key: i, href, className: cls, target: '_blank', rel: 'noreferrer' }, label);
      }
      return part;
    }),
  );
}
